import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  CreditCard, Check, Sparkles, Users, ArrowRight, Crown, Star, Building2, Palette, Minus, Plus
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { HelpTip } from "../components/HelpTip";

const PLANS = [
  {
    id: "estandar", name: "Estándar", pricePerEmployee: 89, icon: Users, popular: false,
    color: "border-slate-200", accent: "bg-slate-600",
    modules: ["Gestión de personal", "Directorio y organigrama", "Vacaciones e incidencias", "Expediente digital", "Comunicación interna", "Mesa de ayuda"],
    description: "Lo esencial para gestionar tu equipo"
  },
  {
    id: "avanzado", name: "Avanzado", pricePerEmployee: 129, icon: Star, popular: true,
    color: "border-blue-500", accent: "bg-blue-600",
    modules: ["Todo de Estándar", "Nómina y pre-nómina", "Control de asistencia", "Onboarding", "Control de activos", "Canal de denuncias", "Firma electrónica"],
    description: "Para empresas que necesitan nómina y control"
  },
  {
    id: "pro", name: "Pro", pricePerEmployee: 159, icon: Crown, popular: false,
    color: "border-violet-500", accent: "bg-violet-600",
    modules: ["Todo de Avanzado", "Reclutamiento con IA", "Psicometría con IA", "Evaluación 360°", "Capacitaciones", "Encuestas NOM-035", "Analytics y reportes IA"],
    description: "La suite completa con inteligencia artificial",
    hasAI: true
  }
];

const WL_PLANS = [
  { id: "wl_20", name: "Hasta 20 empleados", priceYearly: 30000, maxEmp: 20 },
  { id: "wl_50", name: "Hasta 50 empleados", priceYearly: 60000, maxEmp: 50 },
  { id: "wl_unlimited", name: "Más de 50 empleados", priceYearly: 100000, maxEmp: 9999 }
];

const Billing = () => {
  const { user, token } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showWL, setShowWL] = useState(false);
  const [numEmployees, setNumEmployees] = useState(10);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const opts = { headers, withCredentials: true };

  useEffect(() => {
    const load = async () => {
      try {
        const [compRes, payRes] = await Promise.all([
          axios.get(`${API}/company`, opts),
          axios.get(`${API}/payments/history`, opts)
        ]);
        setCompany(compRes.data);
        setPayments(payRes.data || []);
        if (compRes.data?.max_employees) setNumEmployees(compRes.data.max_employees);
      } catch {}
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  const handleCheckout = async (planId) => {
    setProcessingPlan(planId);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        plan_id: planId, num_employees: numEmployees,
        origin_url: window.location.origin
      }, opts);
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al procesar pago");
      setProcessingPlan(null);
    }
  };

  const currentPlan = company?.plan || "estandar";
  const planLabels = { estandar: "Estándar", avanzado: "Avanzado", pro: "Pro", white_label: "White Label" };

  return (
    <Layout title="Planes y Facturación" subtitle="Gestiona tu suscripción">
      {company && (
        <Card className="mb-8 bg-gradient-to-r from-slate-50 to-emerald-50/50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white shadow-sm flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{company.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className="bg-emerald-100 text-emerald-700">{planLabels[currentPlan] || currentPlan}</Badge>
                    <Badge variant="outline">{company.max_employees} empleados</Badge>
                  </div>
                </div>
              </div>
              {company.plan_expires_at && <div className="text-right"><p className="text-sm text-slate-500">Válido hasta</p><p className="font-semibold">{new Date(company.plan_expires_at).toLocaleDateString("es-MX")}</p></div>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Toggle */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-slate-100">
          <Button variant={!showWL ? "default" : "ghost"} onClick={() => setShowWL(false)} className={!showWL ? "bg-white shadow-sm" : ""}>Planes por Empleado</Button>
          <Button variant={showWL ? "default" : "ghost"} onClick={() => setShowWL(true)} className={showWL ? "bg-white shadow-sm" : ""}><Palette className="w-4 h-4 mr-1" />White Label</Button>
        </div>
      </div>

      {!showWL ? (
        <>
          {/* Employee Slider */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="text-sm text-slate-500">Empleados:</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setNumEmployees(Math.max(1, numEmployees - 5))}><Minus className="w-3 h-3" /></Button>
              <Input type="number" className="w-20 text-center" value={numEmployees} onChange={(e) => setNumEmployees(Math.max(1, parseInt(e.target.value) || 1))} />
              <Button size="sm" variant="outline" onClick={() => setNumEmployees(numEmployees + 5)}><Plus className="w-3 h-3" /></Button>
            </div>
            <HelpTip title="Cálculo de Precio"><p>El precio se calcula multiplicando el costo por empleado por el número de empleados.</p><p className="mt-1 italic">Ej: 15 empleados x $89 = $1,335/mes</p></HelpTip>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {PLANS.map((plan) => {
              const isCurrentPlan = currentPlan === plan.id;
              const monthlyTotal = plan.pricePerEmployee * numEmployees;
              const yearlyTotal = monthlyTotal * 12;
              return (
                <Card key={plan.id} className={`relative ${plan.color} ${plan.popular ? "border-2 shadow-lg" : ""}`}>
                  {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-blue-600 text-white px-3">Más Popular</Badge></div>}
                  <CardHeader className="text-center pb-2">
                    <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${plan.popular ? "bg-blue-100" : "bg-slate-100"}`}>
                      <plan.icon className={`w-6 h-6 ${plan.popular ? "text-blue-600" : "text-slate-600"}`} />
                    </div>
                    <CardTitle className="text-xl">{plan.name}</CardTitle>
                    <p className="text-xs text-slate-500">{plan.description}</p>
                    <div className="mt-3">
                      <span className="text-sm text-slate-500">$</span>
                      <span className="text-3xl font-bold">{plan.pricePerEmployee}</span>
                      <span className="text-slate-500 text-sm"> /empleado/mes</span>
                    </div>
                    <div className="mt-1 space-y-0.5">
                      <p className="text-sm font-medium text-emerald-600">${monthlyTotal.toLocaleString("es-MX")}/mes total</p>
                      <p className="text-xs text-slate-400">${yearlyTotal.toLocaleString("es-MX")}/año</p>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-6">
                      {plan.modules.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{f}</li>)}
                      {plan.hasAI && <li className="flex items-center gap-2 text-sm"><Sparkles className="w-4 h-4 text-violet-500 shrink-0" /><span className="text-violet-600 font-medium">Agentes de IA incluidos</span></li>}
                    </ul>
                    <Button
                      className={`w-full ${isCurrentPlan ? "bg-slate-100 text-slate-500" : plan.accent + " hover:opacity-90 text-white"}`}
                      onClick={() => !isCurrentPlan && handleCheckout(plan.id)}
                      disabled={isCurrentPlan || !!processingPlan}
                      data-testid={`subscribe-${plan.id}`}
                    >
                      {processingPlan === plan.id ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : isCurrentPlan ? "Plan Actual" : <>Contratar<ArrowRight className="w-4 h-4 ml-2" /></>}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      ) : (
        <div className="space-y-6 mb-8">
          <div className="text-center max-w-2xl mx-auto mb-6">
            <h2 className="text-2xl font-bold mb-2">White Label - Tu Marca, Tu Sistema</h2>
            <p className="text-slate-500">Obtén Human-OZ con tu propia marca. Todos los módulos incluidos, plan Pro completo con IA. Licencia anual.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {WL_PLANS.map((plan) => (
              <Card key={plan.id} className="border-2 border-violet-200 hover:border-violet-400 transition-colors">
                <CardHeader className="text-center">
                  <Palette className="w-8 h-8 text-violet-600 mx-auto mb-2" />
                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                  <div className="mt-3">
                    <span className="text-3xl font-bold text-violet-700">${plan.priceYearly.toLocaleString("es-MX")}</span>
                    <span className="text-slate-500"> MXN/año</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{plan.maxEmp === 9999 ? "Empleados ilimitados" : `Hasta ${plan.maxEmp} empleados`}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-emerald-500" />Todos los módulos Pro</li>
                    <li className="flex items-center gap-2 text-sm"><Palette className="w-4 h-4 text-violet-500" />Logo y colores personalizados</li>
                    <li className="flex items-center gap-2 text-sm"><Sparkles className="w-4 h-4 text-violet-500" />Agentes de IA incluidos</li>
                    <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-emerald-500" />Dominio personalizado</li>
                    <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-emerald-500" />Soporte prioritario</li>
                  </ul>
                  <Button className="w-full bg-violet-600 hover:bg-violet-700" onClick={() => handleCheckout(plan.id)} disabled={!!processingPlan} data-testid={`subscribe-${plan.id}`}>
                    {processingPlan === plan.id ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>Contratar<ArrowRight className="w-4 h-4 ml-2" /></>}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {payments.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><CreditCard className="w-5 h-5" />Historial de Pagos</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {payments.map((p, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div><p className="font-medium text-sm">{p.plan_name}</p><p className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString("es-MX")}</p></div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold">${(p.amount || 0).toLocaleString("es-MX")} MXN</span>
                    <Badge className={p.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{p.payment_status === "paid" ? "Pagado" : "Pendiente"}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </Layout>
  );
};

export default Billing;
